import { Show, For, useContext, createSignal } from 'solid-js';
import { pdfStore, Collection } from '../../pdf-store';
import { StyledEmptyCollection } from './collection-single.style';
import { CollectionSingle } from './collection-single';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { Text } from '../../../../ui-components/text/text';
import { Heading } from '../../../../ui-components/heading/heading';
import { styled } from 'solid-styled-components';
import { StyledFlexRow } from '../../../../ui-components/utility-style-components/flex';
import { Button } from '../../../../ui-components/button/button';
import { StyledVerticalSpace } from '../../../../ui-components/utility-style-components/spacing';

const StyledCollectionList = styled.div``;

const StyledCTA = styled.div`
    margin-bottom: 4rem;
`;

export const CollectionsList = (props: {
    deleteCollection: (collectionId: string) => void;
    editCollectionAsArrangement: (collection: Collection) => void;
    reset: () => void;
}) => {

    const { 
        localize,
        userState,
        siteInfo,
    } = useContext(AppContext);

    const [ expandAll, setExpandAll ] = createSignal(false);
    const [ expandTimestamp, setExpandTimestamp ] = createSignal(0); // Timestamp to force re-render when expanding/collapsing all collections

    const expandAllCollections = () => {        
        setExpandTimestamp(Date.now());
        setExpandAll(true);
    };

    const collapseAllCollections = () => {
        console.log('Collapsing all collections');
        setExpandTimestamp(Date.now());
        setExpandAll(false);
    };

    const processedCollections = () => {
        if (!pdfStore.collections || !pdfStore.collections.length) {
            return [];
        }

        return pdfStore.collections
            .filter((collection) => {
                // Filter out collections that are not from the current site
                return siteInfo?.siteId === collection.siteInfo?.siteId;
            })
            .toSorted((a, b) => {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
    };

    const CollapseExpandButtons = () => (
        <div>
            <StyledFlexRow gap='1rem' justifyContent='center' alignItems='center'>
                <Button
                    label={localize('pdf-builder-collapse-all', 'Collapse all')}
                    variant='tertiary'
                    small={true}
                    onClick={collapseAllCollections}
                    noBlockSpacing={true}
                />

                <Button
                    label={localize('pdf-builder-expand-all', 'Expand all')}
                    variant='tertiary'
                    small={true}
                    onClick={expandAllCollections}
                    noBlockSpacing={true}
                />
            </StyledFlexRow>
            <StyledVerticalSpace size={2} />
        </div>
    );

    return (
        <StyledCollectionList>

            <Show
                when={!userState.user}
                fallback={
                    <StyledFlexRow justifyContent='center'>
                        <Heading tag="h4" variant='medium'>
                            {localize('pdf-builder-my-documents', 'My Documents')}
                        </Heading>
                        <StyledVerticalSpace size={1} />
                    </StyledFlexRow>
                }
            >
                <StyledCTA>
                    <Heading tag="h3" variant='medium'>
                        {localize('pdf-builder-collections-sign-in-cta-header', 'Sign in to save your PDF collections')}
                    </Heading>
                    <Text fontSize="small" displayRedVerticalLine={false} noBlockSpacing={false}>
                        {localize('pdf-builder-collections-description', 'Create an account to save your PDFs and access them from other devices.')}
                    </Text>
                </StyledCTA>
            </Show>

            <CollapseExpandButtons />

            <Show
                when={processedCollections().length}
                fallback={
                    <StyledEmptyCollection>
                        <Text align="center" fontSize="small" displayRedVerticalLine={false} noBlockSpacing={false} fontStyle='italic'>
                            {localize('pdf-builder-no-compositions', 'No created PDFs yet')}
                        </Text>
                    </StyledEmptyCollection>
                }
            >
                <For each={processedCollections()}>{(collection) => (
                    <CollectionSingle
                        collection={collection as Collection}
                        deleteCollection={props.deleteCollection}
                        editCollection={() => props.editCollectionAsArrangement(collection)}
                        reset={props.reset}
                        isExpanded={expandAll()}
                        expandTimestamp={expandTimestamp()}
                    />
                )}</For>
            </Show>

        </StyledCollectionList>
    );
};